const BASE      = process.env.REACT_APP_API_URL || '/api';
const TOKEN_KEY = 'eo_token';

// ── 401 listeners (AuthContext subscribes to clear state on auth failure) ────
const unauthorizedListeners = new Set();
export const onUnauthorized = (fn) => {
  unauthorizedListeners.add(fn);
  return () => unauthorizedListeners.delete(fn);
};

// ── Core fetch wrapper ────────────────────────────────────────────────────────
// noLogout=true  → 401 throws an error but does NOT fire the logout event.
//                  Use for background polls where a transient 401 shouldn't
//                  kick the user out.
async function req(path, options = {}, customToken, noLogout = false) {
  const token =
    customToken === null
      ? null
      : customToken !== undefined
      ? customToken
      : localStorage.getItem(TOKEN_KEY);

  const headers = { 'Content-Type': 'application/json' };
  // Azure SWA replaces the Authorization header with its own internal token
  // when forwarding to managed functions. Use a custom header instead.
  if (token) headers['X-EO-Token'] = token;

  const res = await fetch(`${BASE}${path}`, { ...options, headers });

  if (res.status === 401) {
    if (!noLogout) {
      unauthorizedListeners.forEach((fn) => fn());
    }
    throw new Error('Session expired. Please log in again.');
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `Request failed (HTTP ${res.status})`);
  }

  return res.json();
}

// ── Auth ──────────────────────────────────────────────────────────────────────
export const register = (data) =>
  req('/auth/register', { method: 'POST', body: JSON.stringify(data) }, null);
export const login = (data) =>
  req('/auth/login', { method: 'POST', body: JSON.stringify(data) }, null);
export const getMe = (token) => req('/auth/me', {}, token);
export const updateMe = (data) => req('/auth/me', { method: 'PUT', body: JSON.stringify(data) });

// ── Public (customer-facing — no auth) ───────────────────────────────────────
export const getPublicBusiness = (businessId) =>
  req(`/public/business/${businessId}`, {}, null);
export const getPublicMenu = (businessId) =>
  req(`/public/menu/${businessId}`, {}, null);
export const createOrder = (businessId, items, totalAmount, customerName, tableNo, notes, orderType, customerEmail) =>
  req(`/public/orders/${businessId}`, {
    method: 'POST',
    body: JSON.stringify({
      items,
      total_amount: totalAmount,
      customer_name: customerName,
      customer_email: customerEmail || '',
      table_no: tableNo || '',
      notes: notes || '',
      order_type: orderType || 'dine-in',
    }),
  }, null);
export const getPublicOrder = (businessId, orderId) =>
  req(`/public/orders/${businessId}/${orderId}`, {}, null);

// ── Vendor tables (auth required) ────────────────────────────────────────────
export const getTables    = ()         => req('/tables');
export const addTable     = (data)     => req('/tables', { method: 'POST', body: JSON.stringify(data) });
export const updateTable  = (id, data) => req(`/tables/${id}`, { method: 'PUT', body: JSON.stringify(data) });
export const deleteTable  = (id)       => req(`/tables/${id}`, { method: 'DELETE' });

// ── Vendor menu (auth required) ───────────────────────────────────────────────
export const getMenu        = ()         => req('/menu');
export const addMenuItem    = (item)     => req('/menu',       { method: 'POST',   body: JSON.stringify(item) });
export const updateMenuItem = (id, item) => req(`/menu/${id}`, { method: 'PUT',    body: JSON.stringify(item) });
export const deleteMenuItem = (id)       => req(`/menu/${id}`, { method: 'DELETE' });

// ── Vendor inventory (auth required) ─────────────────────────────────────────
export const getInventory         = ()         => req('/inventory');
export const addInventoryItem     = (data)     => req('/inventory',       { method: 'POST',   body: JSON.stringify(data) });
export const updateInventoryItem  = (id, data) => req(`/inventory/${id}`, { method: 'PUT',    body: JSON.stringify(data) });
export const deleteInventoryItem  = (id)       => req(`/inventory/${id}`, { method: 'DELETE' });

// ── Vendor customers (auth required) ─────────────────────────────────────────
export const searchCustomers = (q)    => req(`/customers?q=${encodeURIComponent(q)}`);
export const saveCustomer    = (data) => req('/customers', { method: 'POST', body: JSON.stringify(data) });

// ── Email bill — calls PHP relay on itoracle.in directly (Azure blocks SMTP) ──
const MAIL_RELAY = 'https://itoracle.in/scanningo-mail.php';
const RELAY_SECRET = 'scanningo_relay_8043';

export const sendBillEmail = async (to, subject, html, replyTo) => {
  const res = await fetch(MAIL_RELAY, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-Relay-Secret': RELAY_SECRET },
    body: JSON.stringify({ to, subject, html, reply_to: replyTo || '' }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `Email failed (HTTP ${res.status})`);
  }
  return res.json();
};

// ── Retail: Parties ──────────────────────────────────────────────────────────
export const getParties      = ()         => req('/parties');
export const addParty        = (data)     => req('/parties', { method: 'POST', body: JSON.stringify(data) });
export const updateParty     = (id, data) => req(`/parties/${id}`, { method: 'PUT', body: JSON.stringify(data) });
export const deleteParty     = (id)       => req(`/parties/${id}`, { method: 'DELETE' });

// ── Retail: Invoices ─────────────────────────────────────────────────────────
export const getInvoices     = ()         => req('/invoices');
export const addInvoice      = (data)     => req('/invoices', { method: 'POST', body: JSON.stringify(data) });
export const updateInvoice   = (id, data) => req(`/invoices/${id}`, { method: 'PUT', body: JSON.stringify(data) });
export const deleteInvoice   = (id)       => req(`/invoices/${id}`, { method: 'DELETE' });

// ── Retail: Purchases ────────────────────────────────────────────────────────
export const getPurchases    = ()         => req('/purchases');
export const addPurchase     = (data)     => req('/purchases', { method: 'POST', body: JSON.stringify(data) });

// ── Retail: Expenses ─────────────────────────────────────────────────────────
export const getExpenses     = ()         => req('/expenses');
export const addExpense      = (data)     => req('/expenses', { method: 'POST', body: JSON.stringify(data) });
export const updateExpense   = (data)     => req('/expenses', { method: 'PUT',  body: JSON.stringify(data) });
export const deleteExpense   = (data)     => req('/expenses', { method: 'DELETE', body: JSON.stringify(data) });

// ── Vendor orders (auth required) ─────────────────────────────────────────────
// getOrders uses noLogout=true — background polling should not trigger logout
// on a transient 401. The user stays logged in; the next explicit action will
// re-check auth and log out if the session is truly gone.
export const getOrders         = ()             => req('/orders', {}, undefined, true);
export const updateOrderStatus = (id, status)   =>
  req(`/orders/${id}`, { method: 'PUT', body: JSON.stringify({ status }) });
